import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FormField, FormInput, FormSelect } from '@practium/ui';
import type { FarmerFormData, UserPageMode } from '../../../types';
import { STATUS_OPTIONS } from '../../../constants/users.constants';
import { applyPhoneMask, applyDocumentMask } from '../../../utils/masks';
import { useFarmerForm } from '../../../hooks/useFarmerForm';
import styles from '../PersonForm.module.css';

export interface FarmerFormRef {
	submit: () => Promise<void>;
	isDirty: () => boolean;
}

interface FarmerFormProps {
	mode: UserPageMode;
	initialData?: FarmerFormData;
	onSubmit: () => void;
	onLoadingChange: (loading: boolean) => void;
}

export const FarmerForm = forwardRef<FarmerFormRef, FarmerFormProps>(
	function FarmerForm({ mode, initialData, onSubmit, onLoadingChange }, ref) {
		const { form, errors, isLoading, set, handleSubmit, isDirty } = useFarmerForm({
			initialData,
			onSubmit,
		});

		useImperativeHandle(ref, () => ({ submit: handleSubmit, isDirty }));

		useEffect(() => {
			onLoadingChange(isLoading);
		}, [isLoading, onLoadingChange]);

		const isView = mode === 'view';

		if (isView) {
			return (
				<div className={styles.viewGrid}>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Nome</span>
						<span className={styles.viewValue}>{form.name || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Sobrenome</span>
						<span className={styles.viewValue}>{form.surName || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>CPF/CNPJ</span>
						<span className={styles.viewValue}>{form.document || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Telefone (Whatsapp)</span>
						<span className={styles.viewValue}>{form.phoneNumber || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>E-mail</span>
						<span className={styles.viewValue}>{form.email || '—'}</span>
					</div>
					<div className={styles.viewField}>
						<span className={styles.viewLabel}>Status</span>
						<span className={styles.viewValue}>
							{form.status === 'true' ? 'Ativo' : form.status === 'false' ? 'Inativo' : '—'}
						</span>
					</div>
				</div>
			);
		}

		return (
			<div className={styles.formGrid}>
				<FormField label="Nome" required error={errors.name}>
					<FormInput
						value={form.name}
						onChange={e => set('name', e.target.value)}
						placeholder="Ex.: Michael"
						hasError={!!errors.name}
					/>
				</FormField>
				<FormField label="Sobrenome" required error={errors.surName}>
					<FormInput
						value={form.surName}
						onChange={e => set('surName', e.target.value)}
						placeholder="Ex.: Silva"
						hasError={!!errors.surName}
					/>
				</FormField>
				<FormField label="CPF/CNPJ" required error={errors.document}>
					<FormInput
						value={form.document}
						onChange={e => set('document', applyDocumentMask(e.target.value))}
						placeholder="Ex.: xxx.xxx.xxx-xx ou xx.xxx.xxx/xxxx-xx"
						hasError={!!errors.document}
					/>
				</FormField>
				<FormField label="Telefone (Whatsapp)" error={errors.phoneNumber}>
					<FormInput
						value={form.phoneNumber}
						onChange={e => set('phoneNumber', applyPhoneMask(e.target.value))}
						placeholder="Ex.: (xx) xxxxx-xxxx"
						hasError={!!errors.phoneNumber}
					/>
				</FormField>
				<FormField label="E-mail" error={errors.email}>
					<FormInput
						value={form.email}
						onChange={e => set('email', e.target.value)}
						placeholder="Ex.: michael@gmail.com"
						type="email"
						hasError={!!errors.email}
					/>
				</FormField>
				<FormField label="Status" required error={errors.status}>
					<FormSelect
						value={form.status}
						onChange={v => set('status', v)}
						options={STATUS_OPTIONS}
						placeholder="Selecione o Status"
						hasError={!!errors.status}
					/>
				</FormField>
			</div>
		);
	},
);
